
import React, { useEffect, useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellRing } from "lucide-react";
import { useNotification } from '@/contexts/NotificationContext';
import { useTranslation } from "react-i18next";
import { TenderNotification } from '@/hooks/useNotifications';
import { formatDate } from '@/utils/dateFormatters';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TenderDetails } from './TenderDetails';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '../ui/badge';
import { useQuery } from '@tanstack/react-query';
import { TenderData } from '@/types/tender';

// Define interface matching what TenderDetails expects
interface TenderMatch {
  id: string;
  publication_number?: string;
  "notice-title"?: Record<string, string[] | string>;
  "buyer-name"?: Record<string, string[] | string>;
  AU?: any;
  organization_name?: string;
  description?: string;
  tender_data?: any;
  [key: string]: any;
}

export const UnseenTenderNotifications = () => {
  const { t, i18n } = useTranslation();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [unseenTenders, setUnseenTenders] = useState<TenderNotification[]>([]);
  const [selectedTender, setSelectedTender] = useState<TenderMatch | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Function to extract text from multilingual objects or string arrays
  const extractText = (obj: any): string | undefined => {
    if (!obj) return undefined;
    
    if (typeof obj === "string") return obj;
    
    if (Array.isArray(obj)) {
      return obj.length > 0 ? String(obj[0]) : undefined;
    }
    
    if (typeof obj === "object") {
      // Try to get Danish version first since we're using Danish
      if (obj.dan) {
        if (Array.isArray(obj.dan)) {
          return obj.dan.length > 0 ? String(obj.dan[0]) : undefined;
        }
        return String(obj.dan);
      }
      
      // Try to get English version 
      if (obj.eng) {
        if (Array.isArray(obj.eng)) {
          return obj.eng.length > 0 ? String(obj.eng[0]) : undefined;
        }
        return String(obj.eng);
      }
      
      // If no Danish or English, try any other language
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (Array.isArray(value)) {
          if (value.length > 0) return String(value[0]);
        } else {
          return String(value);
        }
      }
    }
    
    return undefined;
  };
  
  // Fetch saved tenders and user seen tenders to identify unseen notifications
  const { data: savedTenders, isLoading: savedTendersLoading } = useQuery({
    queryKey: ['saved-tenders-unseen-notifications'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('saved_tenders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Fetch seen tenders
  const { data: seenTenders, isLoading: seenTendersLoading } = useQuery({
    queryKey: ['seen-tenders-dashboard'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_seen_tenders')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      return data || [];
    }
  });

  useEffect(() => {
    if (!savedTendersLoading && !seenTendersLoading && savedTenders && seenTenders) {
      processUnseenTenders();
    }
  }, [savedTenders, seenTenders, savedTendersLoading, seenTendersLoading]);

  // Process saved tenders to find the unseen ones
  const processUnseenTenders = () => {
    setIsLoading(true);
    
    if (!savedTenders || !seenTenders) {
      setUnseenTenders([]);
      setIsLoading(false);
      return;
    }
    
    // Create a set of seen tender IDs for faster lookup
    const seenTenderIds = new Set(seenTenders.map(item => item.tender_id));
    
    // Process notifications with additional data and mark as read/unread
    const unseenNotifications = savedTenders
      .filter(tender => {
        const tenderData = tender.tender_data as TenderData;
        const tenderId = tenderData?.publication_number || 
                         tenderData?.id || 
                         tender.tender_id || 
                         tender.id;
        return !seenTenderIds.has(tenderId);
      })
      .map(tender => {
        const tenderData = tender.tender_data as TenderData;
        
        // Extract title from various possible sources
        const title = extractText(tenderData?.["notice-title"]) || 
                     extractText(tenderData?.title) || 
                     `Udbud ${tenderData?.id || tender.id}`;
        
        // Extract organization from various possible sources
        const organization = extractText(tenderData?.["buyer-name"]) || 
                           extractText(tenderData?.organization_name) || 
                           extractText(tenderData?.organization) || 
                           "Organisation ikke specificeret";
        
        // Extract deadline date
        const deadlineDate = extractText(tenderData?.["deadline-receipt-tender-date-lot"]) || 
                            extractText(tenderData?.["deadline-date-lot"]) ||
                            extractText(tenderData?.deadline);

        // Create a meaningful ID for the notification
        const tenderId = tenderData?.publication_number || tenderData?.id || tender.tender_id || tender.id;
        
        return {
          id: tender.id,
          tender_data: tenderData,
          created_at: tender.created_at,
          tender_id: tenderId,
          is_read: false,
          title: title,
          organization: organization,
          deadline: deadlineDate
        } as TenderNotification;
      });
    
    setUnseenTenders(unseenNotifications);
    setIsLoading(false);
  };
  
  const handleTenderClick = async (notification: TenderNotification) => {
    // Store selected tender for display in modal - convert TenderData to TenderMatch
    const tenderData = notification.tender_data;
    
    // Create a TenderMatch object with the required properties
    const tenderMatch: TenderMatch = {
      id: notification.id, // Use the notification ID as the required ID property
      ...tenderData, // Spread the tender_data properties
      publication_number: tenderData.publication_number || notification.tender_id?.toString() || notification.id,
      "notice-title": tenderData["notice-title"] || { eng: [notification.title || ""] },
      "buyer-name": tenderData["buyer-name"] || { eng: [notification.organization || ""] },
      organization_name: tenderData.organization_name || notification.organization,
    };
    
    setSelectedTender(tenderMatch);
    setIsDetailsOpen(true);
    
    // Mark as seen in database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      await supabase
        .from('user_seen_tenders')
        .upsert([
          {
            user_id: user.id,
            tender_id: notification.tender_id,
          },
        ]);
      
      // Remove from unseen list
      setUnseenTenders(prev => prev.filter(t => t.tender_id !== notification.tender_id));
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showNotification(t('notifications.errorMarkingAsRead', 'Fejl ved markering af notifikation som læst'), 'error');
    }
  };
  
  // Handle saving the tender
  const handleSaveTender = async () => {
    // This is already handled by the TenderDetails component
    setIsDetailsOpen(false);
  };
  
  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BellRing className={unseenTenders.length > 0 ? "h-5 w-5 text-primary animate-pulse" : "h-5 w-5"} />
            {t('dashboard.notifications.title', 'Notifikationer')}
            {unseenTenders.length > 0 && (
              <Badge className="ml-2 bg-primary">{unseenTenders.length}</Badge>
            )}
          </h2>
        </div>
        
        <div className="mb-2">
          <p className="text-sm text-muted-foreground">
            {t('dashboard.notifications.description', 'Nye udbud der matcher dine præferencer')}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : unseenTenders.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Bell className="mx-auto h-8 w-8 mb-2 opacity-30" />
            <p>{t('dashboard.notifications.noUnseenTenders', 'Ingen nye notifikationer')}</p>
          </div>
        ) : (
          <ScrollArea className="h-[350px] pr-4">
            <div className="space-y-2">
              {unseenTenders.map((notification) => (
                <div 
                  key={notification.id}
                  className="border border-border rounded-lg p-3 hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => handleTenderClick(notification)}
                >
                  <div className="flex items-start">
                    <div className="h-2 w-2 mt-2 bg-primary rounded-full flex-shrink-0 mr-2" />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{notification.title}</h4>
                      <p className="text-xs text-muted-foreground">{notification.organization}</p>
                      
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-muted-foreground">
                          {notification.deadline && (
                            <span>
                              <span className="font-medium">{t('notifications.deadline')}:</span>{" "}
                              {formatDate(notification.deadline)}
                            </span>
                          )}
                        </div>
                        
                        <Button variant="ghost" size="sm" className="h-6 text-xs">
                          {t('dashboard.notifications.viewDetails', 'Se detaljer')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </Card>
      
      {selectedTender && (
        <TenderDetails
          tender={selectedTender}
          isOpen={isDetailsOpen}
          onOpenChange={setIsDetailsOpen}
          onSaveTender={handleSaveTender}
        />
      )}
    </>
  );
};

export default UnseenTenderNotifications;
